import { FirebaseError } from "firebase/app";
import { createLogger } from "./logger";

const log = createLogger("FirebaseErrors");

/** User-friendly messages keyed by Firebase Auth error code. */
const AUTH_ERROR_MAP: Record<string, string> = {
  // Sign-in
  "auth/user-not-found": "Invalid email or password. Please try again.",
  "auth/wrong-password": "Invalid email or password. Please try again.",
  "auth/invalid-credential": "Invalid email or password. Please try again.",
  "auth/invalid-email": "Please enter a valid email address.",
  "auth/user-disabled": "This account has been disabled. Please contact support.",

  // Sign-up
  "auth/email-already-in-use":
    "This email address is already in use. Please try another one or log in.",
  "auth/weak-password": "The password is too weak. Please choose a stronger password.",
  "auth/operation-not-allowed": "This sign-in method is not enabled. Please contact support.",

  // Rate-limiting / abuse
  "auth/too-many-requests":
    "Too many attempts. Please wait a few minutes before trying again.",

  // Password reset
  "auth/expired-action-code": "This reset link has expired. Please request a new one.",
  "auth/invalid-action-code": "This reset link is invalid. Please request a new one.",

  // Network
  "auth/network-request-failed":
    "Network error. Please check your connection and try again.",
};

const GENERIC_MESSAGE = "Something went wrong. Please try again later.";

/**
 * Get a user-friendly error message from any thrown value.
 * Logs the raw error for the developer, returns a safe string for the UI.
 */
export function getAuthErrorMessage(err: unknown, context?: string): string {
  if (err instanceof FirebaseError) {
    const mapped = AUTH_ERROR_MAP[err.code];
    if (mapped) {
      log.warn("Auth error (mapped)", { code: err.code, context });
      return mapped;
    }
    // Unknown Firebase code — log the full error so dev can investigate.
    log.error("Auth error (unmapped)", {
      code: err.code,
      message: err.message,
      context,
    });
    return GENERIC_MESSAGE;
  }

  // Non-Firebase error — always log.
  log.error("Unexpected auth error", {
    error: err instanceof Error ? err.message : String(err),
    context,
  });
  return GENERIC_MESSAGE;
}
